import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext';
import { getSwalTheme, getSwalConfirmTheme } from '../utils/swalTheme';
import { EmptyState, LoadingState, Pagination, PageSwitcher } from '../components/dashboard';
import MedallionListItem from '../components/MedallionListItem';
import AdCard from '../components/AdCard';
import MedallionDetailsModal from '../components/MedallionDetailsModal';
import EditMedallionPostModal from '../components/EditMedallionPostModal';
import api from '../services/api';
import { PAGINATION } from '../constants/limits';
import './MedallionsPage.css';

const MedallionsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detailsModal, setDetailsModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [feedAds, setFeedAds] = useState([]);
  const { user, isUser } = useAuth();

  // Read filters from URL params
  const filtersFromURL = useCallback(() => ({
    my_posts: searchParams.get('my_posts') === 'true',
    listing_type: searchParams.get('listing_type') || ''
  }), [searchParams]);

  const pageFromURL = useCallback(() => {
    const p = searchParams.get('page');
    return p ? parseInt(p, 10) : 1;
  }, [searchParams]);

  const [filters, setFilters] = useState(filtersFromURL);
  const [currentPage, setCurrentPage] = useState(pageFromURL);
  const [paginationInfo, setPaginationInfo] = useState(null);

  // Sync filters from URL when searchParams change
  useEffect(() => {
    setFilters(filtersFromURL());
    setCurrentPage(pageFromURL());
  }, [filtersFromURL, pageFromURL]);

  // Write filter state back to URL
  const updateURLParams = useCallback((newFilters, newPage) => {
    const params = new URLSearchParams();
    if (newFilters.my_posts) params.set('my_posts', 'true');
    if (newFilters.listing_type) params.set('listing_type', newFilters.listing_type);
    if (newPage > 1) params.set('page', newPage.toString());
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    fetchPosts();
  }, [filters, currentPage]);

  // Fetch ads once on mount
  useEffect(() => {
    const fetchAds = async () => {
      try {
        const response = await api.get('/ads?page_type=medallions');
        setFeedAds(response.data.ads || []);
      } catch (err) {
        // Ads are non-critical, fail silently
      }
    };
    fetchAds();
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();

      if (filters.my_posts) {
        params.append('my_posts', 'true');
        params.append('include_expired', 'true');
      }
      if (filters.listing_type) params.append('listing_type', filters.listing_type);

      params.append('page', currentPage.toString());
      params.append('limit', PAGINATION.DEFAULT_LIMIT.toString());

      const response = await api.get(`/medallion-posts?${params.toString()}`);
      setPosts(response.data.posts || []);
      setPaginationInfo(response.data.pagination);
    } catch (err) {
      setError('Failed to load medallion posts');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      updateURLParams(newFilters, 1);
      return newFilters;
    });
  };

  const handleViewDetails = (post) => setDetailsModal(post);

  const handleEdit = (post) => setEditModal(post);

  const handleRenew = async (postId) => {
    try {
      await api.post(`/medallion-posts/${postId}/renew`);
      fetchPosts();
      Swal.fire({
        ...getSwalTheme(),
        icon: 'success',
        title: 'Renewed!',
        text: 'Your post has been renewed for 30 more days.',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      Swal.fire({
        ...getSwalTheme(),
        icon: 'error',
        title: 'Renew Failed',
        text: err.response?.data?.error || 'Failed to renew post.'
      });
    }
  };

  const handleDelete = async (postId) => {
    const result = await Swal.fire({
      ...getSwalConfirmTheme(),
      title: 'Delete Medallion Listing?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      confirmButtonText: 'Yes, delete it'
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/medallion-posts/${postId}`);
      fetchPosts();
      Swal.fire({
        ...getSwalTheme(),
        icon: 'success',
        title: 'Deleted!',
        text: 'Your medallion listing has been deleted.'
      });
    } catch (err) {
      Swal.fire({
        ...getSwalTheme(),
        icon: 'error',
        title: 'Delete Failed',
        text: err.response?.data?.error || 'Failed to delete.'
      });
    }
  };

  const refreshData = () => {
    fetchPosts();
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    updateURLParams(filters, newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="medallions-page">
      <div className="dashboard-header">
        <PageSwitcher />
        {isUser && (
          <div className="header-actions">
            <button
              className={`my-posts-btn ${filters.my_posts ? 'active' : ''}`}
              onClick={() => handleFilterChange('my_posts', !filters.my_posts)}
            >
              My Posts
            </button>
            <Link to="/medallion-listing" className="post-btn">
              + Post a Medallion
            </Link>
          </div>
        )}
      </div>

      <div className="dashboard-layout">
        {/* Sidebar Filters */}
        <aside className="dashboard-sidebar">
          <div className="filter-section">
            <h3 className="filter-title">Listing Type</h3>
            <div className="filter-options">
              <label className={`filter-option ${filters.listing_type === '' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="listing_type"
                  value=""
                  checked={filters.listing_type === ''}
                  onChange={(e) => handleFilterChange('listing_type', e.target.value)}
                />
                <span className="filter-label">All</span>
              </label>
              <label className={`filter-option ${filters.listing_type === 'sell' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="listing_type"
                  value="sell"
                  checked={filters.listing_type === 'sell'}
                  onChange={(e) => handleFilterChange('listing_type', e.target.value)}
                />
                <span className="filter-label">Selling</span>
              </label>
              <label className={`filter-option ${filters.listing_type === 'buy' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="listing_type"
                  value="buy"
                  checked={filters.listing_type === 'buy'}
                  onChange={(e) => handleFilterChange('listing_type', e.target.value)}
                />
                <span className="filter-label">Buying</span>
              </label>
            </div>
          </div>

        </aside>

        {/* Main Content */}
        <main className="dashboard-main">
          {error && <div className="dashboard-error">{error}</div>}

          {loading ? (
            <LoadingState />
          ) : posts.length === 0 ? (
            <EmptyState
              activeTab={filters.listing_type || 'all'}
              isUser={isUser}
            />
          ) : (
            <div className="posts-list">
              {posts.map((post, index) => {
                const postIndex = index + 1;
                const adForSlot = feedAds.find(ad => ad.display_frequency === postIndex);
                return (
                  <React.Fragment key={`medallion-${post.id}`}>
                    {adForSlot && <AdCard ad={adForSlot} />}
                    <MedallionListItem
                      post={post}
                      userRole={user?.role}
                      userId={user?.id}
                      onViewDetails={handleViewDetails}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onRenew={handleRenew}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          )}
          {paginationInfo && paginationInfo.pages > 1 && (
            <Pagination
              pagination={paginationInfo}
              onPageChange={handlePageChange}
            />
          )}
        </main>
      </div>

      {detailsModal && (
        <MedallionDetailsModal
          post={detailsModal}
          onClose={() => setDetailsModal(null)}
          userRole={user?.role}
        />
      )}

      {editModal && (
        <EditMedallionPostModal
          post={editModal}
          onClose={() => setEditModal(null)}
          onUpdate={refreshData}
        />
      )}
    </div>
  );
};

export default MedallionsPage;
